import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import {
  numeroAColumnaExcel,
  agregarValidacionLista,
  configurarProteccionCeldas,
} from '../../../../utils/excelJsUtil';
import TableExcelJsDecorator from '../../../../decorators/tableExcelJsDecorator';
import HeadersExcelJsDecorator from '../../../../decorators/headersExcelJsDecorator';
import TableSubFilasExcelJsDecorator from '../../../../decorators/tableSubFilasExcelJsDecorator';
import {
  DATOS_INSTRUCCIONES_EMPLEADOS,
  CAMPOS_LISTA,
  DATOS_INSTRUCCIONES_NOVEDADES,
  construirCamposListaNovedades,
} from './camposExcel';
import { esquemaEmpleado } from '../schemas/empleadoSchema';
import {
  ALIGN_CENTER,
  BG_CELL_TITLE,
  FONT_TITLE,
} from '../../../../data/excelJsData';

const COLUMNAS_ENCABEZADO = [
  { header: 'Nit', key: 'Nit', width: 15 },
  { header: 'Compañia', key: 'Compañia', width: 40 },
  { header: 'IdSucursal', key: 'IdSucursal', width: 15 },
  { header: 'Sucursal', key: 'Sucursal', width: 40 },
];

const COLUMNAS_INSTRUCCIONES = [
  { header: 'Campo', key: 'campo', width: 25 },
  { header: 'Tipo de dato', key: 'tipoDato', width: 15 },
  { header: 'Descripción', key: 'descripcion', width: 38 },
  { header: 'Formato válido', key: 'formatoValido', width: 35 },
  { header: 'Ejemplo válido', key: 'ejemploValido', width: 35 },
  { header: 'Ejemplo inválido', key: 'ejemploInvalido', width: 38 },
  { header: 'Opciones permitidas', key: 'opcionesPermitidas', width: 30 },
  { header: 'Observaciones', key: 'observaciones', width: 35 },
];

const COLUMNAS_EMPLEADOS = [
  { header: 'Nombres', key: 'nombres', width: 40 },
  { header: 'Primer apellido', key: 'primerApellido', width: 30 },
  { header: 'Segundo apellido', key: 'segundoApellido', width: 30 },
  { header: 'Tipo de documento', key: 'tipoDocumento', width: 20 },
  { header: 'Número de documento', key: 'numeroDocumento', width: 25 },
  { header: 'Cargo', key: 'cargo', width: 40 },
  { header: 'Fecha de ingreso', key: 'fechaIngreso', width: 21 },
  { header: 'Fecha contrato hasta', key: 'fechaContratoHasta', width: 21 },
  { header: 'Teléfono', key: 'telefono', width: 17 },
  { header: 'Salario', key: 'salario', width: 20 },
  { header: 'Tipo de contrato', key: 'tipoContrato', width: 25 },
  { header: 'Embargo', key: 'embargo', width: 15 },
  { header: '¿Insolvente legalmente?', key: 'esInsolvente', width: 25 },
  {
    header: 'Otras deducciones Salario',
    key: 'otrasDeduccionesSalario',
    width: 30,
  },
  {
    header: 'Cantidad llamados atención',
    key: 'cantidadLlamadosAtencion',
    width: 28,
  },
  { header: 'Evaluación desempeño', key: 'evaluacionDesempeno', width: 25 },
  { header: 'Monto máximo cuota', key: 'montoMaximoCuota', width: 25 },
  {
    header: 'Días vacaciones disponibles',
    key: 'diasVacacionesDisponibles',
    width: 28,
  },
  { header: 'Frecuencia de pago', key: 'frecuenciaPago', width: 20 },
  { header: 'Tipo de cuenta bancaria', key: 'tipoCuentaBancaria', width: 25 },
  { header: 'Entidad bancaria', key: 'entidadBancaria', width: 50 },
  {
    header: 'Número de cuenta bancaria',
    key: 'numeroCuentaBancaria',
    width: 30,
  },
];

const COLUMNAS_NOVEDADES = [
  {
    header: 'Número de documento',
    key: 'numeroDocumento',
    width: 40,
    nota: 'Empleado que presenta la novedad. Máximo 15 caracteres',
  },
  {
    header: 'Tipo de novedad',
    key: 'tipoNovedad',
    width: 40,
    nota: 'Seleccione una opción válida',
  },
  {
    header: 'Fecha inicio',
    key: 'fechaInicio',
    width: 21,
    nota: 'Fecha en formato: dd/mm/yyyy Ej: 01/01/2000',
  },
  {
    header: 'Fecha fin',
    key: 'fechaFin',
    width: 21,
    nota: 'Fecha en formato: dd/mm/yyyy Ej: 01/01/2000',
  },
  {
    header: 'Observación',
    key: 'observacion',
    width: 50,
    nota: 'Máximo 255 caracteres',
  },
];

const FORMATO_POR_TIPO_SCHEMA = {
  String: '@',
  Integer: '0',
  Date: 'dd/mm/yyyy',
  Enum: null,
};

const COLUMNAS_MONETARIAS = new Set([
  'salario',
  'montoMaximoCuota',
  'otrasDeduccionesSalario',
]);

const construirMapaFormatosColumnas = () => {
  const mapaFormatos = new Map(
    esquemaEmpleado
      .filter(campo => FORMATO_POR_TIPO_SCHEMA[campo.type] !== null)
      .map(campo => {
        const clave = campo.name.charAt(0).toLowerCase() + campo.name.slice(1);
        const formato = COLUMNAS_MONETARIAS.has(clave)
          ? '#,##0'
          : FORMATO_POR_TIPO_SCHEMA[campo.type];
        return [clave, formato];
      })
  );

  return mapaFormatos;
};

const aplicarFormatosColumnaNovedades = (ws, START_ROW) => {
  const mapaFormatos = construirMapaFormatosColumnas();

  COLUMNAS_EMPLEADOS.forEach(({ key }, indice) => {
    const numeroColumna = indice + 1;
    const formato = mapaFormatos.get(key);

    if (formato) {
      ws.getColumn(numeroColumna).numFmt = formato;
    }
  });
};

const crearHojaEncabezado = (ws, informacionEncabezado, contrasena) => {
  const START_ROW = 1;
  ws.state = 'veryHidden';

  new TableExcelJsDecorator(ws).addTable(START_ROW, COLUMNAS_ENCABEZADO, [
    informacionEncabezado,
  ]);
  ws.protect(String(contrasena), {
    selectLockedCells: true,
    selectUnlockedCells: true,
  });
};

const configurarTituloYSubtituloIntruccionesEmpleados = ws => {
  const ultimaColumnaLetra = numeroAColumnaExcel(COLUMNAS_INSTRUCCIONES.length);

  new HeadersExcelJsDecorator(ws).encabezadoArchivoInstruccionesEmpleados({
    title: 'Instrucciones',
    subtitle:
      'La información suministrada debe cumplir con los siguientes parámetros',
    sectionTitle: 'Hoja Empleados',
    sectionSubtitle:
      'En esta hoja se diligencian los datos de los empleados que se encuentran activos en la empresa',
    rangoHeader: `A1:${ultimaColumnaLetra}1`,
    rangoSubtitle: `A2:${ultimaColumnaLetra}2`,
    rangoSection: `A3:${ultimaColumnaLetra}3`,
    rangoSectionSubtitle: `A4:${ultimaColumnaLetra}4`,
  });
};

const configurarTituloYSubtituloIntruccionesNovedades = (ws, filaInicio) => {
  const ultimaColumnaLetra = numeroAColumnaExcel(COLUMNAS_INSTRUCCIONES.length);

  new HeadersExcelJsDecorator(ws).encabezadoArchivoInstruccionesEmpleados({
    title: 'Hoja Novedades',
    subtitle:
      'En esta hoja se deben diligenciar las novedades de los empleados reportadas durante el mes',
    sectionTitle: '',
    sectionSubtitle: '',
    filaInicio,
    rangoHeader: `A${filaInicio}:${ultimaColumnaLetra}${filaInicio}`,
    rangoSubtitle: `A${filaInicio + 1}:${ultimaColumnaLetra}${filaInicio + 1}`,
    rangoSection: `A${filaInicio + 2}:${ultimaColumnaLetra}${filaInicio + 2}`,
    rangoSectionSubtitle: `A${filaInicio + 3}:${ultimaColumnaLetra}${
      filaInicio + 3
    }`,
  });
};

const crearHojaInstrucciones = (ws, contrasena) => {
  ws.columns = COLUMNAS_INSTRUCCIONES;
  const START_ROW = 5;

  configurarTituloYSubtituloIntruccionesEmpleados(ws);

  new TableSubFilasExcelJsDecorator(ws).addTableConSubfilas(
    START_ROW,
    COLUMNAS_INSTRUCCIONES,
    DATOS_INSTRUCCIONES_EMPLEADOS
  );

  const filaInicioNovedades = ws.lastRow.number + 1;
  configurarTituloYSubtituloIntruccionesNovedades(ws, filaInicioNovedades);

  new TableSubFilasExcelJsDecorator(ws).addTableConSubfilas(
    filaInicioNovedades + 2,
    COLUMNAS_INSTRUCCIONES,
    DATOS_INSTRUCCIONES_NOVEDADES,
    { showHeaders: false }
  );

  ws.protect(String(contrasena), {
    selectLockedCells: true,
    selectUnlockedCells: true,
  });
};

const crearHojaEmpleados = (ws, contrasena) => {
  const START_ROW = 1;
  ws.views = [{ state: 'frozen', ySplit: START_ROW }];

  new TableExcelJsDecorator(ws).addTable(START_ROW, COLUMNAS_EMPLEADOS, [], {
    createEmptyRow: false,
  });

  aplicarFormatosColumnaNovedades(ws, START_ROW);

  configurarProteccionCeldas(ws, COLUMNAS_EMPLEADOS.length, START_ROW);

  CAMPOS_LISTA.forEach(({ key, formulae }) => {
    const indice =
      COLUMNAS_EMPLEADOS.findIndex(columna => columna.key === key) + 1;
    agregarValidacionLista(ws, indice, formulae);
  });

  ws.protect(String(contrasena), {
    selectLockedCells: true,
    selectUnlockedCells: true,
  });
};

const crearHojaNovedades = (ws, contrasena, tiposNovedades) => {
  const ultimaColumnaLetra = numeroAColumnaExcel(COLUMNAS_NOVEDADES.length);

  new TableExcelJsDecorator(ws).addTable(1, COLUMNAS_NOVEDADES, [], {
    createEmptyRow: false,
  });

  ws.insertRow(1, ['Novedades']);
  COLUMNAS_NOVEDADES.forEach((col, index) => {
    if (col.nota) {
      ws.getCell(2, index + 1).note = col.nota;
    }
  });
  ws.mergeCells(`A1:${ultimaColumnaLetra}1`);
  const titleCell = ws.getCell('A1');
  titleCell.alignment = ALIGN_CENTER;
  titleCell.font = FONT_TITLE;
  titleCell.fill = BG_CELL_TITLE;

  construirCamposListaNovedades(tiposNovedades).forEach(({ key, formulae }) => {
    const indice = COLUMNAS_NOVEDADES.findIndex(col => col.key === key) + 1;
    agregarValidacionLista(ws, indice, formulae, 3);
  });

  ws.views = [{ state: 'frozen', ySplit: 2 }];
  configurarProteccionCeldas(ws, COLUMNAS_NOVEDADES.length, 2);

  ws.protect(String(contrasena), {
    selectLockedCells: true,
    selectUnlockedCells: true,
  });
};

const crearHojaBancos = (ws, bancos, contrasena) => {
  const START_ROW = 1;
  const COLUMNAS_BANCOS = [
    { name: 'Código', key: 'codigo', width: 15, alignment: ALIGN_CENTER },
    { name: 'Descripción', key: 'descripcion', width: 70 },
  ];

  new TableExcelJsDecorator(ws).addTable(
    START_ROW,
    COLUMNAS_BANCOS,
    bancos.map(banco => ({
      codigo: banco.codigo,
      descripcion: banco.descripcion,
    })),
    { createEmptyRow: false }
  );

  ws.getRow(1).height = 25;

  ws.insertRow(1, ['Listado de bancos parametrizados']);
  ws.mergeCells('A1:B1');
  const titleCell = ws.getCell('A1');
  titleCell.alignment = ALIGN_CENTER;
  titleCell.font = FONT_TITLE;
  titleCell.fill = BG_CELL_TITLE;

  ws.protect(String(contrasena), {
    selectLockedCells: true,
    selectUnlockedCells: true,
  });
};

export const descargarPlantillaEmpleados = async datos => {
  const { informacionEncabezado, fileName, tiposNovedades } = datos;

  const workbook = new ExcelJS.Workbook();

  const wsEncabezado = workbook.addWorksheet('Encabezado');
  crearHojaEncabezado(
    wsEncabezado,
    informacionEncabezado,
    informacionEncabezado.Nit
  );

  const wsInstrucciones = workbook.addWorksheet('Instrucciones');
  crearHojaInstrucciones(wsInstrucciones, informacionEncabezado.Nit);

  const wsEmpleados = workbook.addWorksheet('Empleados');
  crearHojaEmpleados(wsEmpleados, informacionEncabezado.Nit);

  const wsBancos = workbook.addWorksheet('ListadoBancos');
  crearHojaBancos(wsBancos, datos.bancos, informacionEncabezado.Nit);

  const wsNovedades = workbook.addWorksheet('Novedades');
  crearHojaNovedades(wsNovedades, informacionEncabezado.Nit, tiposNovedades);

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, fileName);
};
